import React, { useState, useRef, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import DatePickerField from "components/DatePickerField";
import { endOfMonth } from "date-fns";
import moment from "moment";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
  faFileCsv,
  faDownload,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const ExportSchema = Yup.object().shape({
  date: Yup.string().required("Please select a month to export"),
});

const AnimalEventExport = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();

  const [exportState, setExportState] = useState({
    status: null,
    progress: 0,
    exportId: null,
    errorMessage: null,
    hasNoData: false,
  });

  const checkRef = useRef(null);
  const previousDateRef = useRef(null);

  useEffect(() => {
    return () => {
      if (checkRef.current) clearInterval(checkRef.current);
    };
  }, []);

  const triggerExport = async (values) => {
    setExportState({
      status: "processing",
      progress: 0,
      exportId: null,
      errorMessage: null,
      hasNoData: false,
    });

    try {
      const response = await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/shelterluv/animal_event_exports",
        body: {
          month: moment(values.date).format("M"),
          year: moment(values.date).format("YYYY"),
        },
      });

      const newExportId = response.data?.id;

      if (newExportId) {
        setExportState((prev) => ({
          ...prev,
          exportId: newExportId,
          progress: 5,
        }));
        startPolling(newExportId);
      } else {
        setExportState({
          status: "failed",
          progress: 0,
          exportId: null,
          errorMessage: "Failed to get export ID from server",
          hasNoData: false,
        });
      }
    } catch (error) {
      const msg = formatUiErrors(error);
      setExportState({
        status: "failed",
        progress: 0,
        exportId: null,
        errorMessage: msg,
        hasNoData: false,
      });
      toast.error(msg);
    }
  };

  const startPolling = (id) => {
    if (checkRef.current) clearInterval(checkRef.current);

    checkRef.current = setInterval(async () => {
      try {
        const res = await apiFetch({
          token,
          dispatch,
          endpoint: `/api/internal/shelterluv/animal_event_exports/${id}/status`,
        });

        const { status, progress, fileReady, errorMessage } =
          res.data?.attributes || {};

        if (status === "completed" || fileReady) {
          clearInterval(checkRef.current);
          const hasNoData =
            errorMessage && errorMessage.toLowerCase().includes("no records");

          setExportState((prev) => ({
            ...prev,
            status: "completed",
            progress: 100,
            errorMessage: errorMessage || null,
            hasNoData,
          }));

          if (hasNoData) {
            toast.info(
              "Export completed but no data found for the selected month."
            );
          } else {
            toast.success("Your event data export is ready!");
          }
        } else if (status === "failed") {
          clearInterval(checkRef.current);
          setExportState((prev) => ({
            ...prev,
            status: "failed",
            errorMessage: errorMessage || "Export failed. Please try again.",
            hasNoData: false,
          }));
          toast.error(errorMessage || "Export failed. Please try again.");
        } else {
          setExportState((prev) => ({
            ...prev,
            status: "processing",
            progress: progress || prev.progress,
          }));
        }
      } catch (err) {
        clearInterval(checkRef.current);
        setExportState((prev) => ({
          ...prev,
          status: "failed",
          errorMessage:
            "Unable to check export status. Please refresh the page.",
          hasNoData: false,
        }));
      }
    }, 2000);
  };

  const handleDownload = async () => {
    if (!exportState.exportId) return;

    try {
      const savedToken = JSON.parse(localStorage.getItem("@sac-token"));

      const xhr = new XMLHttpRequest();
      xhr.open(
        "GET",
        `/api/internal/shelterluv/animal_event_exports/${exportState.exportId}/download`,
        true
      );
      xhr.setRequestHeader("access-token", savedToken.accessToken);
      xhr.setRequestHeader("client", savedToken.client);
      xhr.setRequestHeader("uid", savedToken.uid);
      xhr.responseType = "blob";

      xhr.onload = function () {
        if (this.status === 200) {
          const blob = new Blob([this.response], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;

          const contentDisposition = xhr.getResponseHeader(
            "Content-Disposition"
          );
          let filename = "animal_events_export.csv";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, "").trim();
            }
          }

          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success("File downloaded successfully");
        } else {
          toast.error("Download failed. Please try again.");
        }
      };

      xhr.onerror = function () {
        toast.error(
          "Network error during download. Please check your connection."
        );
      };

      xhr.send();
    } catch (e) {
      toast.error("Download error: " + e.message);
    }
  };

  const resetExport = () => {
    setExportState({
      status: null,
      progress: 0,
      exportId: null,
      errorMessage: null,
      hasNoData: false,
    });
  };

  const handleDateChange = (currentDate) => {
    if (
      previousDateRef.current &&
      previousDateRef.current !== currentDate &&
      (exportState.status === "completed" || exportState.status === "failed")
    ) {
      resetExport();
    }
    previousDateRef.current = currentDate;
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Animal Event Data Export Tool</h1>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm leading-relaxed">
        <p>
          This tool allows you to download a CSV containing the animal
          event-level data SAC used to calculate your monthly counts. Select a
          month to export, and we will generate a file that includes only the
          records for your organization. This can help you confirm how animals
          were mapped and ensure your monthly reporting aligns with your
          internal data.
        </p>
      </div>

      <div className="bg-white rounded p-6 border shadow-sm">
        <Formik
          initialValues={{ date: "" }}
          validationSchema={ExportSchema}
          onSubmit={triggerExport}
        >
          {({ isValid, dirty, values }) => {
            if (values.date) {
              handleDateChange(values.date);
            }

            return (
              <Form>
                <div className="mb-6 max-w-md">
                  <DatePickerField
                    label="Select Month"
                    name="date"
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    className="form-input w-full"
                    maxDate={endOfMonth(new Date())}
                    required
                    placeholderText="e.g., June 2025"
                  />
                </div>

                <div className="mt-6 border-t pt-6">
                  {exportState.status === "processing" && (
                    <div className="mb-6">
                      <div className="flex items-center text-blue-600 mb-3">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-lg"
                        />
                        <span className="font-medium">Generating file...</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${exportState.progress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {exportState.progress}% complete
                      </p>
                    </div>
                  )}

                  {exportState.status === "completed" &&
                    !exportState.hasNoData && (
                      <div className="bg-green-50 p-5 rounded border border-green-200 mb-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start text-green-700">
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="mr-3 text-2xl mt-1"
                            />
                            <div>
                              <p className="font-bold text-lg mb-1">
                                Your event data export is ready.
                              </p>
                              <p className="text-sm">
                                Download your CSV to review the records used in
                                SAC's reporting for this month.
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={handleDownload}
                            className="ml-4 flex-shrink-0"
                            emphasis="primary"
                          >
                            <FontAwesomeIcon
                              icon={faDownload}
                              className="mr-2"
                            />
                            Download CSV
                          </Button>
                        </div>
                      </div>
                    )}

                  {exportState.status === "completed" &&
                    exportState.hasNoData && (
                      <div className="bg-yellow-50 p-5 rounded border border-yellow-200 mb-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start text-yellow-800">
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="mr-3 text-2xl mt-1 flex-shrink-0"
                            />
                            <div>
                              <p className="font-bold text-lg mb-1">
                                No data found for selected month
                              </p>
                              <p className="text-sm mb-3">
                                No animal event records were found for the
                                selected month. This could mean:
                              </p>
                              <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                                <li>No events occurred during this period</li>
                                <li>
                                  Data for this month hasn't been imported yet
                                </li>
                                <li>
                                  This month is outside your organization's
                                  active period
                                </li>
                              </ul>
                              <p className="text-sm mt-3">
                                Please try selecting a different month or
                                contact support if you believe this is an error.
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={resetExport}
                            className="ml-4 flex-shrink-0"
                            emphasis="secondary"
                          >
                            Try Another Month
                          </Button>
                        </div>
                      </div>
                    )}

                  {exportState.status === "failed" && (
                    <div className="bg-red-50 p-4 rounded border border-red-200 mb-6">
                      <div className="flex items-start text-red-700">
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          className="mr-3 text-xl mt-0.5"
                        />
                        <div>
                          <p className="font-bold mb-1">Export failed</p>
                          <p className="text-sm">
                            {exportState.errorMessage ||
                              "An error occurred. Please try again or contact support if the issue persists."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {exportState.status !== "completed" && (
                    <div className="flex justify-start">
                      <Button
                        type="submit"
                        disabled={
                          !isValid ||
                          !dirty ||
                          exportState.status === "processing"
                        }
                        isLoading={exportState.status === "processing"}
                        loadingText="Starting Export..."
                      >
                        <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                        Export Event Data
                      </Button>
                    </div>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default AnimalEventExport;
